<template>
    <v-dialog 
        v-model="dialog" 
        max-width="800"
        persistent
        :retain-focus="false"
        transition="dialog-bottom-transition"
    >
        <v-card class="damaged-dialog-card" :loading="loading">
            <!-- Close Button - Fixed Position Top Right -->
            <v-btn 
                icon 
                large
                color="white"
                @click="closeModal"
                class="close-button-fixed"
            >
                <v-icon size="28" color="grey darken-2">mdi-close</v-icon>
            </v-btn>
            
            <!-- Enhanced Header -->
            <v-card-title 
                class="enhanced-damaged-header white--text d-flex align-center pa-6"
                :class="{ 'reset-mode': resetOnly }"
            >
                <div class="d-flex align-center">
                    <v-avatar color="white" size="48" class="mr-4">
                        <v-icon color="orange" size="28">
                            {{ resetOnly ? 'mdi-refresh-circle' : 'mdi-alert-octagon' }}
                        </v-icon>
                    </v-avatar>
                    <div>
                        <h2 class="text-h4 font-weight-bold mb-1">
                            {{ resetOnly ? 'Auftrag zurücksetzen' : 'Fehlgravur melden' }}
                        </h2>
                        <p class="text-subtitle-1 mb-0 opacity-90">
                            {{ resetOnly 
                                ? 'Der Packlisteneintrag wird gelöscht und der Auftrag wird wieder in die Produktion zurückgeschleust.' 
                                : 'Der Packlisteneintrag wird gelöscht und der Auftrag wird wieder in die Produktion zurückgeschleust. Zusätzlich wird automatisch ein Eintrag in der Fehlproduktion-Datenbank angelegt.'
                            }}
                        </p>
                    </div>
                </div>
            </v-card-title>

            <!-- Enhanced Content -->
            <v-card-text class="enhanced-content pa-6">

                <!-- Error Display -->
                <v-alert
                    v-if="error"
                    type="error"
                    dismissible
                    @input="error = null"
                    class="mb-4"
                >
                    <strong>Fehler beim Verarbeiten:</strong> {{ error }}
                </v-alert>

                <!-- Products Section -->
                <div class="d-flex justify-space-between align-center mb-4">
                        <h3 class="text-h6 font-weight-bold d-flex align-center">
                            <v-icon color="orange" class="mr-2">mdi-package-variant</v-icon>
                            Inhalt der Bestellung
                            <v-chip 
                                color="orange" 
                                small 
                                outlined 
                                class="ml-3"
                            >
                                {{ trashData.items?.length || 0 }} Artikel
                            </v-chip>
                        </h3>
                    </div>

                    <!-- Enhanced Product List -->
                    <div v-if="trashData.items && trashData.items.length > 0" class="product-grid">
                        <v-card
                            v-for="item in trashData.items"
                            :key="item.sku"
                            class="product-item elevation-2"
                            :class="{ 'selected-product': item.trash }"
                            @click.stop="toggleProductSelection(item)"
                        >
                            <!-- Product Image (if available) -->
                            <div v-if="getImageUrl(item)" class="product-image-container">
                                <img :src="getImageUrl(item)" class="product-image" />
                            </div>
                            
                            <v-card-text class="pa-4">
                                <div class="d-flex align-center justify-space-between">
                                    <div class="flex-grow-1 mr-3">
                                        <div class="product-sku mb-2">{{ item.sku }}</div>
                                        
                                        <!-- Quantity Display -->
                                        <div v-if="item.qty" class="quantity-display mb-2">
                                            <v-chip color="orange" small dark>
                                                <v-icon left small>mdi-counter</v-icon>
                                                Menge: {{ item.qty }}
                                            </v-chip>
                                        </div>
                                        
                                        <!-- Filtered Attributes Display (only line attributes) -->
                                        <div v-if="getFilteredAttributes(item).length > 0" class="attributes-display">
                                            <div class="attributes-preview">
                                                <div 
                                                    v-for="attr in getFilteredAttributes(item)" 
                                                    :key="attr.key"
                                                    class="attribute-item"
                                                >
                                                    <span class="attribute-label">{{ attr.key }}:</span>
                                                    <span class="attribute-value">{{ attr.value }}</span>
                                                </div>
                                            </div>
                                        </div>
                                        <div v-else class="no-attributes grey--text text-caption">
                                            Keine relevante Personalisierung vorhanden
                                        </div>
                                    </div>
                                    
                                    <!-- Selection Checkbox -->
                                    <div class="selection-area">
                                        <v-checkbox
                                            v-model="item.trash"
                                            color="orange"
                                            class="selection-checkbox ma-0"
                                            hide-details
                                            :ripple="false"
                                            @click.stop
                                        ></v-checkbox>
                                    </div>
                                </div>
                            </v-card-text>
                        </v-card>
                    </div>

                    <!-- Empty State -->
                    <div v-else class="empty-state">
                        <v-avatar size="64" color="grey lighten-3" class="mb-3">
                            <v-icon size="32" color="grey">mdi-package-variant-remove</v-icon>
                        </v-avatar>
                        <h4 class="text-h6 grey--text mb-2">Keine Produkte verfügbar</h4>
                        <p class="text-body-2 grey--text">
                            In dieser Bestellung wurden keine Produkte gefunden.
                        </p>
                    </div>

                    <!-- Selection Summary -->
                    <div class="selection-summary mt-4" v-if="selectedCount > 0">
                        <v-chip 
                            color="orange" 
                            dark
                            large
                            class="px-4"
                        >
                            <v-icon left>mdi-check-circle</v-icon>
                            {{ selectedCount }} von {{ trashData.items?.length || 0 }} ausgewählt
                        </v-chip>
                    </div>
            </v-card-text>

            <!-- Enhanced Footer -->
            <v-divider></v-divider>
            
            <v-card-actions class="enhanced-footer pa-6">
                <div class="d-flex align-center justify-end w-100">
                    <div class="action-buttons">
                        <v-btn 
                            text 
                            large
                            @click="closeModal"
                            class="mr-3"
                        >
                            <v-icon left>mdi-close</v-icon>
                            Abbrechen
                        </v-btn>
                        <v-btn 
                            color="orange"
                            large
                            :loading="loading"
                            @click="applyTrash"
                            class="px-6"
                            dark
                        >
                            <v-icon left>
                                {{ resetOnly ? 'mdi-refresh-circle' : 'mdi-alert-octagon' }}
                            </v-icon>
                            {{ resetOnly ? 'Auftrag zurücksetzen' : 'Fehlgravur eintragen' }}
                            <span v-if="selectedCount > 0" class="ml-2">({{ selectedCount }})</span>
                        </v-btn>
                    </div>
                </div>
            </v-card-actions>
        </v-card>
    </v-dialog>
</template>

<script>
import packlistService from "@/services/PacklistService";
import EventBus from "@/event-bus";

export default {
    name: 'DamagedDialog',
    data() {
        return {
            loading: false,
            error: null,
            dialog: false,
            success: false,
            listName: null,
            trashData: { orderId: null, items: [] },
            resetOnly: false
        }
    },
    computed: {
        selectedCount() {
            const count = this.trashData.items?.filter(x => x.trash).length || 0;
            console.log('Current selectedCount:', count);
            return count;
        },
    },
    methods: {
        closeModal() {
            this.dialog = false;
            // Reset state when closing
            this.error = null;
            this.loading = false;
        },
        toggleProductSelection(item) {
            // Toggle the selection when clicking on the card
            console.log('🔄 TOGGLING SELECTION - Item:', item.sku, 'Current state:', item.trash);
            console.log('🔄 Full item object:', item);
            this.$set(item, 'trash', !item.trash);
            console.log('🔄 After toggle - New state:', item.trash);
            console.log('🔄 Full trashData after toggle:', this.trashData);
            console.log('🔄 All items in trashData:', this.trashData.items);
        },
        getImageUrl(item) {
            // Get image from various possible sources (like ArticleCard)
            let hasDesignId = item.designId || (item.attributes && item.attributes.designId);
            let hasPreview = item.attributes && item.attributes.preview;

            if (hasDesignId) {
                return `https://accelery-poster.s3.eu-central-1.amazonaws.com/${encodeURIComponent(hasDesignId)}.png`;
            } else if (hasPreview) {
                return hasPreview[0]?.url || hasPreview;
            } else if (item.attributes && typeof item.attributes === 'object') {
                // Look for any URL in attributes (especially line1)
                for (const [key, value] of Object.entries(item.attributes)) {
                    if (typeof value === 'string' && this.isImageUrl(value)) {
                        return value;
                    }
                }
            }
            return null;
        },
        
        getFilteredAttributes(item) {
            if (!item.attributes || typeof item.attributes !== 'object') return [];
            
            return Object.entries(item.attributes)
                .filter(([key, value]) => {
                    // Only show attributes that include 'line' in the key name
                    // Skip image URLs since we display them separately
                    const isLineAttribute = key.toLowerCase().includes('line');
                    const isNotImageUrl = !this.isImageUrl(value);
                    
                    return isLineAttribute && isNotImageUrl;
                })
                .map(([key, value]) => ({ key, value }));
        },
        
        isImageUrl(url) {
            if (!url || typeof url !== 'string') return false;
            
            try {
                const urlObj = new URL(url);
                // Check if it's a valid HTTP/HTTPS URL
                if (!['http:', 'https:'].includes(urlObj.protocol)) return false;
                
                // Check if the URL ends with a common image extension
                const imageExtensions = ['.jpg', '.jpeg', '.png', '.gif', '.webp', '.svg', '.bmp'];
                const pathname = urlObj.pathname.toLowerCase();
                return imageExtensions.some(ext => pathname.endsWith(ext));
            } catch (e) {
                return false;
            }
        },
        async applyTrash() {
            try {
                console.log('');
                console.log('═══════════════════════════════════════');
                console.log('🗑️ APPLY TRASH CALLED');
                console.log('═══════════════════════════════════════');
                
                this.loading = true;

                if (!this.trashData || !this.trashData.items || !this.trashData.orderId) {
                    console.error('❌ No valid order data available');
                    this.error = 'No valid order data available';
                    return;
                }

                let selectedItems = this.trashData.items.filter(x => x.trash === true);
                let orderId = this.trashData.orderId;

                console.log('📦 Order ID:', orderId);
                console.log('📦 Total items in dialog:', this.trashData.items.length);
                console.log('📦 Selected items count:', selectedItems.length);
                console.log('');
                console.log('✅ SELECTED ITEMS (what will be sent to server):');
                selectedItems.forEach((item, index) => {
                    console.log(`   ${index + 1}. SKU: ${item.sku}`);
                    console.log(`      - Has shelf: ${!!item.shelf}`);
                    console.log(`      - Status: ${item.status}`);
                    console.log(`      - Has translated: ${!!item.translated}`);
                });
                console.log('');
                console.log('🔧 API CALL DETAILS:');
                console.log('   URL: POST /v2/packlist/trash/' + orderId + '?resetOnly=' + this.resetOnly);
                console.log('   Body (selectedItems):', selectedItems);
                console.log('   resetOnly flag:', this.resetOnly);
                console.log('═══════════════════════════════════════');

                if (selectedItems.length === 0) {
                    this.error = 'Bitte wählen Sie mindestens einen Artikel aus';
                    return;
                }
                
                await packlistService.applyTrash(orderId, selectedItems, this.resetOnly);
                
                console.log('✅ Trash applied successfully - refreshing packlist...');
                
                // Emit refresh event and wait for processing
                EventBus.$emit("REFRESH_PACKLIST");
                
                // Wait a moment for the refresh to process before closing dialog
                await new Promise(resolve => setTimeout(resolve, 500));
                
            } catch (e) {
                this.error = e.message;
                console.error('❌ Error applying trash:', e);
            } finally {
                this.loading = false;
                this.dialog = false;
            }
        }
    },
    watch: {
        'trashData.items': {
            handler(newItems) {
                if (!newItems) return;
                
                console.log('');
                console.log('🔄 ITEMS SELECTION CHANGED');
                console.log('━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━');
                console.log('📊 Current selection state:');
                console.log('Raw items data:', newItems);
                newItems.forEach((item, index) => {
                    if (item.trash) {
                        console.log(`   ✅ ${index + 1}. ${item.sku} - SELECTED`);
                    } else {
                        console.log(`   ⬜ ${index + 1}. ${item.sku} - not selected`);
                    }
                });
                console.log('');
                console.log('📈 Total selected:', newItems.filter(x => x.trash).length, 'of', newItems.length);
                console.log('━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━');
                console.log('');
            },
            deep: true
        }
    },
    mounted() {
        EventBus.$on("OPEN_DAMAGED_DIALOG", (data) => {
            console.log('📋 OPENING DAMAGED DIALOG (with fehlgravur)');
            console.log('📋 Received data:', data);
            console.log('📋 Data structure:', JSON.stringify(data, null, 2));
            this.dialog = true;
            this.resetOnly = false;
            this.trashData = data;
            
            // Initialize trash property for all items
            if (this.trashData.items) {
                console.log('📋 Initializing trash property for', this.trashData.items.length, 'items');
                this.trashData.items.forEach((item, index) => {
                    console.log(`📋 Item ${index}:`, item.sku, 'Setting trash to false');
                    this.$set(item, 'trash', false);
                });
            }
            
            console.log('📋 Dialog state - resetOnly:', this.resetOnly, 'items:', this.trashData.items?.length);
        });
        EventBus.$on("OPEN_DAMAGED_DIALOG_RESETONLY", (data) => {
            console.log('📋 OPENING DAMAGED DIALOG (RESET ONLY)');
            console.log('📋 Received data:', data);
            console.log('📋 Data structure:', JSON.stringify(data, null, 2));
            this.dialog = true;
            this.resetOnly = true;
            this.trashData = data;
            
            // Initialize trash property for all items
            if (this.trashData.items) {
                console.log('📋 Initializing trash property for', this.trashData.items.length, 'items');
                this.trashData.items.forEach((item, index) => {
                    console.log(`📋 Item ${index}:`, item.sku, 'Setting trash to false');
                    this.$set(item, 'trash', false);
                });
            }
            
            console.log('📋 Dialog state - resetOnly:', this.resetOnly, 'items:', this.trashData.items?.length);
        });
    }
}
</script>

<style scoped>
.damaged-dialog-card {
    border-radius: 16px;
    overflow: hidden;
}

/* Close Button Fixed Position */
.close-button-fixed {
    position: absolute !important;
    top: 12px !important;
    right: 12px !important;
    z-index: 100 !important;
    background-color: rgba(255, 255, 255, 0.9) !important;
    backdrop-filter: blur(10px);
    box-shadow: 0 2px 8px rgba(0, 0, 0, 0.15) !important;
}

.close-button-fixed:hover {
    background-color: rgba(255, 255, 255, 1) !important;
    transform: scale(1.05);
}

.enhanced-damaged-header {
    background: linear-gradient(135deg, #ff9800 0%, #f57c00 100%);
    position: relative;
    z-index: 10;
}

/* Keep same orange theme for all modes */
.enhanced-damaged-header.reset-mode {
    background: linear-gradient(135deg, #ff9800 0%, #f57c00 100%);
}

.enhanced-content {
    background-color: #fafafa;
    min-height: 200px;
}

.info-section {
    margin-bottom: 24px;
}


.product-grid {
    display: grid;
    grid-template-columns: repeat(auto-fit, minmax(300px, 1fr));
    gap: 16px;
    margin-bottom: 16px;
}

.product-item {
    transition: all 0.3s ease;
    border-radius: 12px;
    border: 2px solid transparent;
    cursor: pointer;
    position: relative;
    background-color: white;
}

.product-item:hover {
    transform: translateY(-2px);
    box-shadow: 0 8px 24px rgba(0,0,0,0.15) !important;
    border-color: rgba(255, 152, 0, 0.3);
}

.selected-product {
    border-color: #ff9800;
    background: linear-gradient(135deg, rgba(255, 152, 0, 0.05) 0%, rgba(255, 152, 0, 0.02) 100%);
    box-shadow: 0 4px 16px rgba(255, 152, 0, 0.3) !important;
}

.selected-product::before {
    content: "";
    position: absolute;
    top: 0;
    right: 0;
    width: 0;
    height: 0;
    border-style: solid;
    border-width: 0 24px 24px 0;
    border-color: transparent #ff9800 transparent transparent;
    z-index: 1;
}

/* Product Image Container */
.product-image-container {
    width: 100%;
    height: 200px;
    background-color: #f5f5f5;
    display: flex;
    align-items: center;
    justify-content: center;
    overflow: hidden;
    position: relative;
}

.product-image {
    max-width: 100%;
    max-height: 100%;
    object-fit: contain;
}

.product-sku {
    font-family: 'Roboto Mono', monospace;
    font-size: 1.1rem;
    font-weight: 600;
    color: #1976d2;
    background-color: #e3f2fd;
    padding: 4px 12px;
    border-radius: 6px;
    display: inline-block;
}

.quantity-display {
    display: flex;
    align-items: center;
}

.attributes-display {
    margin-top: 12px;
}

.attributes-preview {
    background-color: #f8f9fa;
    border-radius: 8px;
    padding: 12px;
    border: 1px solid rgba(0,0,0,0.05);
}

.attribute-item {
    display: flex;
    margin-bottom: 6px;
    font-size: 0.9rem;
    line-height: 1.4;
}

.attribute-item:last-child {
    margin-bottom: 0;
}

.attribute-label {
    font-weight: 600;
    color: #424242;
    min-width: 80px;
    margin-right: 12px;
    flex-shrink: 0;
}

.attribute-value {
    color: #212121;
    word-break: break-word;
    flex: 1;
}

.attribute-image-container {
    margin-top: 8px;
    border-radius: 8px;
    overflow: hidden;
    border: 1px solid rgba(0,0,0,0.1);
    background-color: #f5f5f5;
}

.attribute-image {
    max-width: 120px;
    max-height: 80px;
    width: auto;
    height: auto;
    display: block;
    object-fit: cover;
}

.attribute-text {
    font-family: 'Roboto Mono', monospace;
    font-size: 0.85rem;
    color: #424242;
    white-space: pre-wrap;
    line-height: 1.4;
    background-color: #f5f5f5;
    padding: 8px;
    border-radius: 4px;
}

.no-attributes {
    font-style: italic;
    padding: 12px;
    text-align: center;
    color: rgba(0,0,0,0.5);
}

.selection-area {
    display: flex;
    align-items: center;
    justify-content: center;
}

.selection-checkbox {
    transform: scale(1.5);
}

.selection-summary {
    display: flex;
    justify-content: center;
    margin-top: 16px;
}

.empty-state {
    display: flex;
    flex-direction: column;
    align-items: center;
    justify-content: center;
    text-align: center;
    min-height: 200px;
    padding: 40px;
}


.enhanced-footer {
    background-color: white;
    border-top: 1px solid #e0e0e0;
}


.action-buttons {
    display: flex;
    align-items: center;
}

/* Loading state styling */
.damaged-dialog-card.v-card--loading {
    pointer-events: none;
}

.damaged-dialog-card.v-card--loading::before {
    background-color: rgba(255, 255, 255, 0.8);
}

/* Responsive adjustments */
@media (max-width: 900px) {
    .product-grid {
        grid-template-columns: 1fr;
    }
    
    .enhanced-damaged-header {
        padding: 16px !important;
    }
    
    .enhanced-content {
        padding: 16px !important;
    }
    
    .enhanced-footer {
        padding: 16px !important;
        flex-direction: column;
        gap: 16px;
    }
    
    .action-buttons {
        width: 100%;
        justify-content: space-between;
    }
    
    .action-buttons .v-btn {
        flex: 1;
        max-width: 200px;
    }
}

@media (max-width: 600px) {
    .text-h4 {
        font-size: 1.5rem !important;
    }
    
    
    .product-grid {
        gap: 12px;
    }
}

/* Smooth animations */
.v-enter-active, .v-leave-active {
    transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1);
}

.v-enter, .v-leave-to {
    opacity: 0;
    transform: scale(0.9);
}

/* Enhanced alert styling */
.v-alert--prominent {
    box-shadow: 0 2px 8px rgba(0,0,0,0.1);
}

/* Improve checkbox interaction */
.selection-checkbox input {
    cursor: pointer;
}

/* Product item hover effects */
.product-item:active {
    transform: translateY(0px) scale(0.98);
    transition: transform 0.1s ease;
}
</style>
